import { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { MessageSeverity } from "primereact/api";
import { classNames } from "primereact/utils";

import { faMessages, faTrash, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { ActionButton } from "../../../../../components/Shared/Buttons";
import { TooltipWrapper } from "../../../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { Icon } from "../../../../../components/Shared/Icon/Icon";
import { confirmAction } from "../../../../../components/Shared/Models/Functions";
import { ShowTooltipWhenTextTruncated } from "../../../../../components/Shared/ShowTooltipWhenTruncated/ShowTooltipWhenTruncated";
import { translationKeys } from "../../../../../models/app-translation-keys";
import { IChatThreadExtendedModel } from "../../../../../models/interfaces";
import { useThreadService } from "../../../../../services/ServiceHooks";
import { ICustomKnowledgeBaseReadChatbot } from "../../../../../stores";
import { useStore } from "../../../../../stores/StoreContext";
import { KnowledgeBaseTypeIcon } from "../../../ChatActionBar/KnowledgeBaseSelection/KnowledgeBaseTypeIcon/KnowledgeBaseTypeIcon";

import "./ThreadItem.scss";

interface IThreadItemProps {
  thread: IChatThreadExtendedModel;
  knowledgeBase: ICustomKnowledgeBaseReadChatbot | undefined;
  isActive: boolean;
}

const RawThreadItem: FunctionComponent<IThreadItemProps> = (props: IThreadItemProps) => {
  const { t } = useTranslation();
  const { threadStore, sidebarStore, toastStore } = useStore();
  const threadService = useThreadService();
  const { visibleToUser, threadSummary } = props.thread;

  // NOTE #63781 if the user is anonymous and the active thread has a pending prompt, disable the ability to select another thread because the current thread does not exist on BE yet and switching threads will cause the pending prompt to be lost
  const isDisabled = sidebarStore.isAnonymous && !!threadStore.activeThread?.pendingPrompt;

  const isKnowledgeBaseAvailable: boolean = !!props.thread.knowledgeBaseId && !!props.knowledgeBase;

  const chatBotHistoryTranslationKeys = translationKeys.feature.chatbot.history;

  const threadSummaryRef = useRef(null);
  const knowledgeBaseRef = useRef(null);

  const selectActiveThread = async (thread: IChatThreadExtendedModel) => {
    if (isDisabled) {
      return;
    }

    await threadService.openThread(thread);
  };

  const deleteThread = async (): Promise<void> => {
    const deletionKeys = translationKeys.thread.confirmDelete;

    const confirmResult = await confirmAction(t, {
      header: t(deletionKeys.title),
      message: t(deletionKeys.body, {
        chatName: threadSummary || t(translationKeys.thread.error.noSummaryAvailable),
      }),
      acceptLabel: t(deletionKeys.acceptLabel),
      rejectLabel: t(deletionKeys.rejectLabel),
      closeOnEscape: true,
      dismissableMask: true,
      defaultFocus: "reject",
    });

    if (!confirmResult) {
      return;
    }

    try {
      await threadService.deleteThreadById(props.thread.id!);

      toastStore.addToast({
        summary: { translationKey: translationKeys.toast.summarySuccess },
        detail: { message: t(deletionKeys.successDetail) },
        severity: MessageSeverity.SUCCESS,
      });

      requestAnimationFrame(() => {
        const sidebarItems = document.querySelectorAll<HTMLElement>(".thread-list .thread-item");
        const nextSidebarItem = Array.from(sidebarItems).find(item => item.dataset.threadId !== props.thread.id);
        (nextSidebarItem || document.querySelector<HTMLElement>(".thread-list, .no-threads"))?.focus();
      });
    } catch {
      toastStore.addToast({
        summary: { translationKey: translationKeys.toast.summaryError },
        detail: { message: t(deletionKeys.errorDetail) },
        severity: MessageSeverity.ERROR,
      });
    }
  };

  const handleDeleteThreadClick = async (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    await deleteThread();
  };

  return (
    <div
      className={classNames("thread-item sidebarThreadSummaryTooltip p-ripple", {
        "is-active": props.isActive,
        "is-disabled": isDisabled,
      })}
      onClick={() => selectActiveThread(props.thread)}
      tabIndex={isDisabled ? -1 : 0}
      onKeyDown={event => {
        if (event.target !== event.currentTarget || event.key !== "Enter") {
          return;
        }

        selectActiveThread(props.thread);
      }}
      aria-disabled={isDisabled}
      role="button"
      data-thread-id={props.thread.id}
    >
      <TooltipWrapper targetRef={threadSummaryRef} content={threadSummary} showDelay={300} />

      <ActionButton
        id="delete-thread-button"
        customClasses="thread-item-delete-button"
        icon={faTrash}
        active={props.isActive}
        removeSpaces={false}
        buttonDisabled={isDisabled}
        onClick={handleDeleteThreadClick}
        tooltip={t(translationKeys.thread.confirmDelete.deleteButtonLabel)}
        tooltipPosition="right"
      />

      <div className="thread-item-summary" ref={threadSummaryRef}>
        <div className="thread-item-icon">
          <Icon icon={faMessages} iconSize="xs" color={props.isActive ? "inverse" : "muted"} />
        </div>

        <div
          className={classNames("thread-item-label", {
            deleted: !visibleToUser,
          })}
        >
          {threadSummary?.length ? (
            threadSummary
          ) : (
            <span className="no-summary-available">{t(translationKeys.thread.error.noSummaryAvailable)} </span>
          )}
        </div>
      </div>

      {!!props.thread.knowledgeBaseId && (
        <div className="thread-item-knowledge-base" ref={knowledgeBaseRef}>
          <TooltipWrapper
            targetRef={knowledgeBaseRef}
            content={t(chatBotHistoryTranslationKeys.threadItem.tooltip.knowledgeBaseNotFound)}
            disabled={isKnowledgeBaseAvailable}
            showDelay={300}
          />

          <div className="thread-item-icon">
            {isKnowledgeBaseAvailable ? (
              <KnowledgeBaseTypeIcon
                isSpoKnowledgebase={props.knowledgeBase!.isSpoKnowledgeBase}
                compact={true}
                isActive={props.isActive}
              />
            ) : (
              <Icon icon={faTriangleExclamation} iconSize="xs" color={props.isActive ? "warning-light" : "warning"} />
            )}
          </div>

          <div className="thread-item-label">
            {isKnowledgeBaseAvailable ? (
              <ShowTooltipWhenTextTruncated tooltipContent={props.knowledgeBase!.name} tooltipPosition="right">
                <small className="thread-item-knowledge-base-label">{props.knowledgeBase!.name}</small>
              </ShowTooltipWhenTextTruncated>
            ) : (
              <small className="thread-item-knowledge-base-warning">
                {t(chatBotHistoryTranslationKeys.threadItem.errors.knowledgeBaseNotFound)}
              </small>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ThreadItem = observer(RawThreadItem);
